import isEmpty from "lodash/isEmpty";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { InternalButton } from "@sharedComponents/buttons";

import AddToBagBtn from "@components/store/product-detail/shared/add-to-bag";
import AddToBagLockerBtn from "@components/store/product-detail/shared/add-to-bag-locker-item";

import useInternationalRestrictionsModal from "@hooks/product-detail/use-international-restrictions-modal";
import useUSRestrictionsModal from "@hooks/product-detail/use-us-restriction-modal";

import { useProductDetailContext } from "@contexts/product-detail-context";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useSendItemsContext } from "@contexts/send-items-context";
import { getIsLockerShop } from "@selectors/store";
import { getCartOrder } from "@selectors/cart";

import { isCollectionTypeProduct, isMedleyProduct, isSwagProduct } from "@helpers/products/types";

import styles from "./main.module.scss";

const emptyArr = [];
const emptyObj = {};

const useShowPrefrenceRestrictionModal = ({
  product,
  selectedVariant = emptyObj,
  isBYOProduct = false,
  showBulkOptions = false,
}) => {
  const { isSendToMyself } = useSendItemsContext();

  const { editProductIds: [editProductId] = emptyArr } = useProductDetailContext() || emptyObj;

  const { productSets = [], lineItems = [] } = useSelector(getCartOrder);
  const isLockerShop = useSelector(getIsLockerShop);
  const { isGuestOrPreviewMode } = usePreviewModeContext();

  const {
    askForRestriction = false,
    askForPreference,
    askForPreferenceMessage,
    intent,
    bulkAvailable,
    hasTierPricing,
  } = product;

  const { isOptionSelected, setOptionSelectionError, isColorSelected, setColorSelectionError } =
    useProductDetailContext();

  const {
    restrictions,
    dietaryRestriction,
    dietaryPreference: cartAddedProductPreference,
  } = useMemo(() => {
    // incase of collection we only use existing restrictions if we are updating
    // for other products we will show existing in case of adding also as it will be same lineItem
    if (isCollectionTypeProduct(intent)) {
      const orderSets = isGuestOrPreviewMode ? lineItems : productSets;
      if (editProductId && !isEmpty(orderSets)) {
        return orderSets.find(({ id }) => id === editProductId);
      }
    } else {
      return lineItems.find(
        ({ productId }) => String(productId) === String(selectedVariant?.externalId),
      );
    }
  }, [editProductId, isGuestOrPreviewMode, productSets, lineItems, selectedVariant]) || {};

  const cartAddedProductDietaryRestriction = dietaryRestriction?.[0];

  const { toggleRestrictionsModal, renderRestrictionsModal } = useInternationalRestrictionsModal({
    previouslySelectedRestriction: cartAddedProductDietaryRestriction || "",
    previouslySelectedPreference: cartAddedProductPreference || "",
    askForRestriction,
    askForPreference,
    askForPreferenceMessage,
    isSwagProduct: isSwagProduct(intent),
  });

  const {
    toggleRestrictionsModal: toggleMedlyDietryRestrictionsModal,
    renderRestrictionsModal: renderMedlyDietryRestrictionsModal,
  } = useUSRestrictionsModal(restrictions || emptyArr);

  const getProductRestrictionModal = () => {
    if (isMedleyProduct(intent) && !askForPreference) return toggleMedlyDietryRestrictionsModal();
    return toggleRestrictionsModal();
  };

  const restrictionModeAddToBagClickHandler = ({ isColorAndSizeSelectionAvailable = true }) => {
    if (isColorAndSizeSelectionAvailable && isSwagProduct(intent) && !isColorSelected) {
      setColorSelectionError(true);
      return;
    }
    if (isColorAndSizeSelectionAvailable && !isLockerShop && !isOptionSelected && !bulkAvailable) {
      setOptionSelectionError(true);
      return;
    }
    return getProductRestrictionModal();
  };

  const showAddItemButton =
    !isBYOProduct &&
    isSendToMyself &&
    (askForRestriction || askForPreference || isMedleyProduct(intent));

  const renderAddToBagBtn = (btnProps) => {
    const {
      isColorAndSizeSelectionAvailable = true,
      btnLabel = "",
      btnParentClass = "",
      isDisabled,
    } = btnProps;

    const ctaText = (() => {
      if (btnLabel) return btnLabel;
      return editProductId ? "Update Bag" : "Add to Bag";
    })();

    if (showAddItemButton)
      return (
        <div className={btnParentClass}>
          <InternalButton
            styleType="primary"
            className={styles.addToBagBtn}
            onClick={() => {
              restrictionModeAddToBagClickHandler({ isColorAndSizeSelectionAvailable });
            }}
            isDisabled={isDisabled}
            disabled={isDisabled}
          >
            {ctaText}
          </InternalButton>
        </div>
      );
    if (bulkAvailable || isLockerShop || (showBulkOptions && hasTierPricing))
      return <AddToBagLockerBtn {...btnProps} />;
    return <AddToBagBtn {...btnProps} />;
  };

  // This has been added to get input on cart page from line item card
  const renderExistingCartItemSelectOptionBtn = () => {
    if (showAddItemButton)
      return (
        <InternalButton
          styleType="link"
          onClick={getProductRestrictionModal}
          className={styles.selectBtn}
        >
          Select option to continue
        </InternalButton>
      );
  };

  return {
    renderRestrictionsModal,
    renderMedlyDietryRestrictionsModal,
    renderAddToBagBtn,
    renderExistingCartItemSelectOptionBtn,
    toggleRestrictionsModal,
  };
};
export default useShowPrefrenceRestrictionModal;
